import React, {
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ClassWithChildren } from "@helpers/class";
import { BaseCellProps } from "@components/spreadsheet/cells";
import { stopPropagation } from "@components/spreadsheet/cells/cellEditorEvents";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import { useClasses } from "@api/class.api";
import { ClassCombobox } from "./ClassCombobox";
import styles from "./TransactionSplitClassCell.module.scss";

type Props = BaseCellProps<string[]>;

export const TransactionSplitClassCell: React.FC<Props> = ({
    value,
    isEditing,
    finishEditing,
    rowIdx,
    colId,
    enterEditModeKey,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [draftClassIds, setDraftClassIds] = useState<string[]>(
        Array.isArray(value) ? value : [],
    );
    // A Tab pick lands in the same keydown that commits, before a re-render.
    const latestDraftRef = useRef(draftClassIds);
    const updateDraft = useCallback((classIds: string[]) => {
        latestDraftRef.current = classIds;
        setDraftClassIds(classIds);
    }, []);
    const triggerClassName = useMemo(
        () => `class-cell-trigger-${rowIdx}-${colId}`,
        [rowIdx, colId],
    );
    const { classesMap, sortedClassesIndexMap } = useClasses();

    useLayoutEffect(() => {
        updateDraft(Array.isArray(value) ? value : []);
    }, [value, isEditing, updateDraft]);

    const selectedClasses = useMemo(() => {
        if (!draftClassIds.length) {
            return [];
        }
        return draftClassIds
            .map((id) => classesMap.get(id))
            .filter(Boolean)
            .toSorted((a, b) => {
                const aIdx = sortedClassesIndexMap.get(a!.id) ?? Infinity;
                const bIdx = sortedClassesIndexMap.get(b!.id) ?? Infinity;
                return aIdx - bIdx;
            }) as ClassWithChildren[];
    }, [draftClassIds, classesMap, sortedClassesIndexMap]);

    useLayoutEffect(() => {
        if (isEditing && containerRef.current) {
            const trigger = containerRef.current.querySelector(
                `.${triggerClassName}`,
            ) as HTMLElement;
            trigger?.click();
        }
    }, [isEditing, triggerClassName]);

    if (isEditing) {
        return (
            <>
                <div
                    ref={containerRef}
                    onClick={stopPropagation}
                    onMouseDown={stopPropagation}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "Tab") {
                            finishEditing(latestDraftRef.current);
                        }
                        if (e.key === "Escape") {
                            finishEditing();
                        }
                    }}
                >
                    <ClassCombobox
                        selectedClassIds={draftClassIds}
                        onChange={updateDraft}
                        onOpenChange={(open) => {
                            if (!open) {
                                finishEditing(latestDraftRef.current);
                            }
                        }}
                        cellEditor={{ openingKey: enterEditModeKey }}
                        hideArrow
                        triggerButtonProps={{
                            variant: "primary",
                            size: "sm",
                            className: triggerClassName,
                            style: {
                                position: "absolute",
                                opacity: 0,
                                pointerEvents: "none",
                            },
                        }}
                    />
                </div>
                <ClassValueDisplay classes={selectedClasses} />
            </>
        );
    }

    return <ClassValueDisplay classes={selectedClasses} />;
};

const ClassValueDisplay: React.FC<{ classes: ClassWithChildren[] }> = ({
    classes,
}) => {
    if (classes.length === 0) {
        return null;
    }

    return (
        <div className={styles.classCell}>
            <ClassAvatarGroup classes={classes} max={3} />
        </div>
    );
};
